import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function RecurringTransfers() {
  const [transfers, setTransfers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    from_account_id: '',
    beneficiary_name: '',
    to_iban: '',
    amount: '',
    frequency: 'monthly',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  const token = localStorage.getItem('token');

  const frequencyLabels = {
    weekly: 'Hebdomadaire',
    monthly: 'Mensuel',
    quarterly: 'Trimestriel',
    yearly: 'Annuel'
  };

  const statusLabels = {
    active: { label: 'Actif', color: 'green' },
    paused: { label: 'Pausé', color: 'yellow' },
    cancelled: { label: 'Annulé', color: 'red' },
    completed: { label: 'Terminé', color: 'gray' }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [transfersRes, accountsRes] = await Promise.all([
        axios.get(`${API_URL}/recurring-transfers`, config),
        axios.get(`${API_URL}/accounts`, config)
      ]);
      setTransfers(transfersRes.data);
      setAccounts(accountsRes.data.filter(acc => acc.status === 'active'));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/recurring-transfers`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, config);
      alert('Virement récurrent créé avec succès !');
      setShowForm(false);
      setFormData({
        from_account_id: '',
        beneficiary_name: '',
        to_iban: '',
        amount: '',
        frequency: 'monthly',
        description: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancel = async (transferId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce virement récurrent ?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/recurring-transfers/${transferId}`, config);
      alert('Virement récurrent annulé avec succès');
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePauseResume = async (transferId, currentStatus) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await axios.patch(`${API_URL}/recurring-transfers/${transferId}`, { status: newStatus }, config);
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Virements Récurrents</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          {showForm ? 'Annuler' : '+ Nouveau virement récurrent'}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
        <div className="flex items-start">
          <span className="text-2xl mr-3">ℹ️</span>
          <div>
            <p className="font-semibold text-blue-900">Virements automatiques programmés</p>
            <p className="text-sm text-blue-700">
              Configurez des virements qui seront exécutés automatiquement selon la fréquence choisie. 
              Idéal pour les loyers, abonnements, épargne automatique, etc.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Configurer un virement récurrent</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Compte à débiter *
                </label>
                <select
                  value={formData.from_account_id}
                  onChange={(e) => setFormData({ ...formData, from_account_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Sélectionner un compte</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_type} - {acc.balance.toFixed(2)} € - N° {acc.account_number_display || acc.reference_number}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du bénéficiaire *
                </label>
                <input
                  type="text"
                  value={formData.beneficiary_name}
                  onChange={(e) => setFormData({ ...formData, beneficiary_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Propriétaire, EDF, etc."
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IBAN du bénéficiaire *
              </label>
              <input
                type="text"
                value={formData.to_iban}
                onChange={(e) => setFormData({ ...formData, to_iban: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fréquence *
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="weekly">Hebdomadaire (chaque semaine)</option>
                  <option value="monthly">Mensuel (chaque mois)</option>
                  <option value="quarterly">Trimestriel (tous les 3 mois)</option>
                  <option value="yearly">Annuel (chaque année)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de début *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fin (optionnel)
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min={formData.start_date}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description / Objet *
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Loyer mensuel, Épargne automatique, etc."
                required
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Créer le virement récurrent
            </button>
          </form>
        </div>
      )}

      {/* Transfers List */}
      <div className="grid gap-6">
        {transfers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <p className="text-gray-500">Aucun virement récurrent configuré</p>
          </div>
        ) : (
          transfers.map((transfer) => {
            const statusInfo = statusLabels[transfer.status];
            return (
              <div
                key={transfer.id}
                className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🔄</span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{transfer.description}</h3>
                      <p className="text-sm text-gray-500">
                        Vers: {transfer.beneficiary_name} • 
                        <span className={`ml-2 px-2 py-1 bg-${statusInfo.color}-100 text-${statusInfo.color}-700 rounded-full text-xs`}>
                          {statusInfo.label}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">
                      {transfer.amount.toFixed(2)} €
                    </p>
                    <p className="text-sm text-gray-500">
                      {frequencyLabels[transfer.frequency]}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-200 mb-4">
                  <div>
                    <p className="text-xs text-gray-600">Prochaine exécution</p>
                    <p className="font-semibold">
                      {new Date(transfer.next_execution).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Exécutions</p>
                    <p className="font-semibold">{transfer.executions_count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Date de début</p>
                    <p className="font-semibold">
                      {new Date(transfer.start_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Date de fin</p>
                    <p className="font-semibold">
                      {transfer.end_date ? new Date(transfer.end_date).toLocaleDateString('fr-FR') : 'Indéterminée'}
                    </p>
                  </div>
                </div>

                {transfer.status !== 'cancelled' && transfer.status !== 'completed' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePauseResume(transfer.id, transfer.status)}
                      className={`flex-1 px-4 py-2 ${
                        transfer.status === 'active' 
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      } rounded-lg font-medium transition-colors`}
                    >
                      {transfer.status === 'active' ? '⏸️ Mettre en pause' : '▶️ Reprendre'}
                    </button>
                    <button
                      onClick={() => handleCancel(transfer.id)}
                      className="flex-1 px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium transition-colors"
                    >
                      ❌ Annuler
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default RecurringTransfers;
